import { envSettings } from '@ami/angular/core/env-settings';
import { EventTypes, ToastService } from '@ami/angular/core/toasts';
import { APP_INITIALIZER, DestroyRef, EnvironmentProviders, Injector, effect, inject, makeEnvironmentProviders } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { filter, take } from 'rxjs';
import { checkDeployedVersionEffect } from './states/effects/check-deployed-version.effect';
import { AppVersionStore } from './states/stores/app-version.store';
import { reportVersionReloadProblem } from './version-reload-diagnostics';

export interface VersionAutoReloadOptions {
  /** Version baked into the running bundle (NX_REACT_APP_AMI_VERSION). Empty disables the check. */
  appVersion: string | undefined;
  /** URL of the deployed version file. Default: /assets/amiVersion.json */
  versionUrl?: string;
  /** Periodic check interval. Default: 5 minutes. */
  intervalMs?: number;
  /** Delay before the first check after startup. Default: 15 seconds. */
  initialDelayMs?: number;
  /** Minimum time between checks triggered by the tab becoming visible. Default: 30 seconds. */
  visibilityThrottleMs?: number;
}

const DEFAULTS = {
  versionUrl: '/assets/amiVersion.json',
  intervalMs: 5 * 60 * 1000,
  initialDelayMs: 15 * 1000,
  visibilityThrottleMs: 30 * 1000,
};

/**
 * Optional runtime overrides, intended for manual testing only. Set them as NX_* environment
 * variables when serving locally (e.g. NX_APP_VERSION_CHECK_INTERVAL_MS=15000) to shorten the
 * timings without touching production defaults. Absent or non-numeric values are ignored.
 */
const ENV_OVERRIDES: Record<string, 'intervalMs' | 'initialDelayMs' | 'visibilityThrottleMs'> = {
  NX_APP_VERSION_CHECK_INTERVAL_MS: 'intervalMs',
  NX_APP_VERSION_CHECK_INITIAL_DELAY_MS: 'initialDelayMs',
  NX_APP_VERSION_CHECK_VISIBILITY_THROTTLE_MS: 'visibilityThrottleMs',
};

/**
 * Reloads open sessions when a newer front-end version has been deployed, and tells the user
 * afterwards. Add once to an app's providers.
 *
 * Background: after the 2.20.0 release, tabs open since before the deploy kept running the old
 * bundle and every order save was rejected by the new API. Nothing forced those sessions to refresh.
 */
export function provideVersionAutoReload(options: VersionAutoReloadOptions): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: APP_INITIALIZER,
      useFactory: () => initializeVersionAutoReload(options),
      deps: [AppVersionStore, ToastService, Router, Injector, DestroyRef],
      multi: true,
    },
  ]);
}

function initializeVersionAutoReload(options: VersionAutoReloadOptions) {
  const store = inject(AppVersionStore);
  const toasts = inject(ToastService);
  const router = inject(Router);
  const injector = inject(Injector);
  const destroyRef = inject(DestroyRef);
  const appInsights = inject(ApplicationInsights, { optional: true });

  return () => {
    try {
      const running = (options.appVersion ?? '').trim();
      if (!running) {
        console.warn('[version-reload] No app version in this bundle; automatic reload on new versions is disabled.');
        return;
      }

      const settings = { ...DEFAULTS, ...readEnvOverrides(), ...options };
      store.initialize(running, settings.versionUrl);

      const updated = store.consumeReloadMarker();
      if (updated) {
        afterFirstNavigation(router, () => toasts.queueToast(`Updated to version ${updated.to}`, EventTypes.Success));
      }

      // When the loop breaker trips, ask the user to do what the automatic reload could not.
      effect(() => {
        const manual = store.state().manualReloadRequired;
        if (!manual) return;
        afterFirstNavigation(router, () =>
          toasts.queueToast(
            // The toast body is rendered as HTML, so the instruction sits on its own line.
            `A new version (${manual.deployed}) is available but this page could not update itself.<br><br>Please close this browser tab and open the site again.`,
            EventTypes.Warning
          )
        );
      }, { injector });

      startTriggers(store, settings, appInsights, destroyRef);
    } catch (error) {
      reportVersionReloadProblem(appInsights, 'Initialization failed; automatic reload is disabled for this session', error);
    }
  };
}

/** The toast host lives in the routed layout, so wait for the first navigation before queueing. */
function afterFirstNavigation(router: Router, action: () => void) {
  const run = () => window.setTimeout(action, 500);
  if (router.navigated) {
    run();
    return;
  }
  router.events.pipe(filter((event) => event instanceof NavigationEnd), take(1)).subscribe(run);
}

function startTriggers(store: AppVersionStore, settings: typeof DEFAULTS, appInsights: ApplicationInsights | null, destroyRef: DestroyRef) {
  let lastCheck = 0;

  const check = (reason: string) => {
    try {
      lastCheck = Date.now();
      // runEffect returns the effect's observable; nothing runs until it is subscribed.
      store.runEffect(checkDeployedVersionEffect, reason).subscribe();
    } catch (error) {
      reportVersionReloadProblem(appInsights, 'Could not start a version check', error, { reason });
    }
  };

  const onVisibilityChange = () => {
    if (document.visibilityState !== 'visible') return;
    if (Date.now() - lastCheck < settings.visibilityThrottleMs) return;
    check('tab became visible');
  };

  const startupTimer = window.setTimeout(() => check('startup'), settings.initialDelayMs);
  const intervalTimer = window.setInterval(() => check('interval'), settings.intervalMs);
  document.addEventListener('visibilitychange', onVisibilityChange);

  // The root injector lives as long as the app, so this matters for test hosts and teardown only.
  destroyRef.onDestroy(() => {
    window.clearTimeout(startupTimer);
    window.clearInterval(intervalTimer);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  });
}

function readEnvOverrides(): Partial<typeof DEFAULTS> {
  const overrides: Partial<typeof DEFAULTS> = {};
  for (const [envKey, optionKey] of Object.entries(ENV_OVERRIDES)) {
    const raw = envSettings(envKey, { optional: true });
    const value = raw === undefined ? NaN : Number(raw);
    if (Number.isFinite(value) && value > 0) {
      overrides[optionKey] = value;
      console.log(`[version-reload] ${optionKey} overridden to ${value}ms via ${envKey}`);
    }
  }
  return overrides;
}
